import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";

/**
 * Rolling social-proof ticker.
 * - Fetches /api/checkout/social-proof (public, cached 30s upstream).
 * - Cycles one anonymised purchase every 4s with a subtle fade.
 * - Pauses rotation on hover.
 * - Renders nothing if the API returns 0 items or errors out.
 */
export default function SocialProofTicker() {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(true);
  const pausedRef = useRef(false);

  useEffect(() => {
    let alive = true;
    api.get("/checkout/social-proof")
      .then(({ data }) => {
        if (!alive) return;
        setItems(Array.isArray(data?.recent_purchases) ? data.recent_purchases : []);
      })
      .catch(() => { if (alive) setItems([]); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (items.length <= 1) return; // nothing to rotate
    const tick = setInterval(() => {
      if (pausedRef.current) return;
      setVisible(false);
      setTimeout(() => {
        setIdx((i) => (i + 1) % items.length);
        setVisible(true);
      }, 260);
    }, 4000);
    return () => clearInterval(tick);
  }, [items.length]);

  if (!items.length) return null;

  const current = items[idx % items.length];
  const suffix = current.minutes_ago <= 0
    ? "just now"
    : (current.minutes_ago >= 60 ? "an hour ago" : `${current.minutes_ago}m ago`);

  return (
    <div
      className="ftg-social-proof"
      data-testid="social-proof-ticker"
      onMouseEnter={() => { pausedRef.current = true; }}
      onMouseLeave={() => { pausedRef.current = false; }}
      aria-live="polite"
    >
      <span className="ftg-social-proof__dot" aria-hidden="true" />
      <span
        className={`ftg-social-proof__text ${visible ? "is-visible" : "is-hidden"}`}
        data-testid="social-proof-text"
      >
        {current.display_name} · {current.location} · bought {current.tier_label} · {suffix}
      </span>
    </div>
  );
}
